import React, { useState } from 'react';
import DiagnosticHeader from './DiagnosticHeader';

function InputForm({ formData, setFormData, onGenerateReport }) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s-]+$/;
    if (!name) return 'This field is required';
    if (!nameRegex.test(name)) return 'Only letters, spaces, and hyphens allowed';
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateRole = (role) => {
    if (!role) return 'Please select your role';
    return '';
  };

  const validateValueAtStake = (value) => {
    if (!value || value <= 0) return 'Value must be greater than 0';
    return '';
  };

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [id]: newValue
    }));

    let error = '';
    if (id === 'firstName' || id === 'lastName') {
      error = validateName(newValue);
    } else if (id === 'email') {
      error = validateEmail(newValue);
    } else if (id === 'role') {
      error = validateRole(newValue);
    } else if (id === 'valueAtStake') {
      error = validateValueAtStake(newValue);
    }

    setErrors(prev => ({
      ...prev,
      [id]: error
    }));
  };

  const handleBlur = (id) => {
    setTouched(prev => ({
      ...prev,
      [id]: true
    }));
  };

  const isSectionAComplete = 
    formData.firstName && !errors.firstName &&
    formData.lastName && !errors.lastName &&
    formData.email && !errors.email &&
    formData.gdprConsent &&
    formData.role && !errors.role;

  const isSectionBComplete = formData.valueAtStake > 0 && !errors.valueAtStake;
  const isFormComplete = isSectionAComplete && isSectionBComplete;

  return (
    <>
      <DiagnosticHeader showNav={false} />
      <div className="max-w-5xl mx-auto p-8 pt-28">
        {/* Header */}
        <div className="bg-white rounded-lg p-10 mb-8 border border-gray-200 text-center">
          <h1 className="text-4xl font-bold text-[#152C47] mb-3 font-['Poppins']">
            Hidden Work Diagnostic
          </h1>
          <div className="text-[#697A90] text-lg mb-3">5-Minute Mini Diagnostic</div>
          <div className="text-[#697A90] text-sm italic">
            Operational truth-telling for transformation leaders
          </div>
        </div>

        {/* Section A: About You */}
        <div className="bg-white rounded-lg p-8 mb-8 border border-gray-200">
          <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
            About You
          </h2>

          <p className="text-sm text-[#697A90] mb-6 italic">
            Fields marked with <span className="text-[#86C2CA]">*</span> are required
          </p>

          <div className="mb-6">
            <label className="block font-semibold text-gray-800 mb-2">
              First name <span className="text-[#86C2CA]">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              onBlur={() => handleBlur('firstName')}
              className={`w-full p-3 bg-gray-50 border rounded text-gray-800 ${
                touched.firstName && errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {touched.firstName && errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block font-semibold text-gray-800 mb-2">
              Last name <span className="text-[#86C2CA]">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              onBlur={() => handleBlur('lastName')}
              className={`w-full p-3 bg-gray-50 border rounded text-gray-800 ${
                touched.lastName && errors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {touched.lastName && errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block font-semibold text-gray-800 mb-2">
              Email <span className="text-[#86C2CA]">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={() => handleBlur('email')}
              className={`w-full p-3 bg-gray-50 border rounded text-gray-800 ${
                touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {touched.email && errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block font-semibold text-gray-800 mb-2">
              GDPR <span className="text-[#86C2CA]">*</span>
            </label>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="gdprConsent"
                checked={formData.gdprConsent}
                onChange={handleInputChange}
                className="mt-1 w-4 h-4 border-2 border-gray-300 rounded"
              />
              <span className="text-gray-600 text-sm">
                I agree to allow Daring Futures to store my data for their use only. See our{' '}
                <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Privacy+and+Cookies" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#86C2CA] underline">
                  Privacy Policy
                </a> and{' '}
                <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Data+Retention" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#86C2CA] underline">
                  Data Retention
                </a> policy for more information. <span className="text-[#86C2CA]">*</span>
              </span>
            </div>
            {!formData.gdprConsent && touched.gdprConsent && (
              <p className="text-red-500 text-sm mt-1">You must accept the privacy policy</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block font-semibold text-gray-800 mb-2">
              Your role <span className="text-[#86C2CA]">*</span>
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={handleInputChange}
              onBlur={() => handleBlur('role')}
              className={`w-full p-3 bg-gray-50 border rounded text-gray-800 h-12 ${
                touched.role && errors.role ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">• Your role (default)</option>
              <option value="Senior Leader">Senior Leader</option>
              <option value="Transformation Leader">Transformation Leader</option>
            </select>
            {touched.role && errors.role && (
              <p className="text-red-500 text-sm mt-1">{errors.role}</p>
            )}
            <div className="text-[#697A90] text-sm mt-2 italic">
              Are you leading the transformation, or shaping the context that the transformation operates within?
            </div>
          </div>
        </div>

        {/* Section B: About Your Transformation */}
        <div className={`bg-white rounded-lg p-8 mb-8 border border-gray-200 ${!isSectionAComplete ? 'opacity-50 pointer-events-none' : ''}`}>
          <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
            About Your Transformation
          </h2>

          <div className="mb-6">
            <label className="block font-semibold text-gray-800 mb-2">
              Value at stake (£M) <span className="text-[#86C2CA]">*</span>
            </label>
            <input
              type="number"
              id="valueAtStake"
              value={formData.valueAtStake || ''}
              onChange={handleInputChange}
              onBlur={() => handleBlur('valueAtStake')}
              min="0"
              step="0.1"
              className={`w-full p-3 bg-gray-50 border rounded text-gray-800 ${
                touched.valueAtStake && errors.valueAtStake ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={!isSectionAComplete}
            />
            {touched.valueAtStake && errors.valueAtStake && (
              <p className="text-red-500 text-sm mt-1">{errors.valueAtStake}</p>
            )}
            <div className="text-[#697A90] text-sm mt-2 italic">
              This is the value your transformation business case is designed to realise. Must be greater than 0 to take this diagnostic.
            </div>
          </div>
        </div>

        {/* Section C: About Your Situation */}
        <div className={`bg-white rounded-lg p-8 mb-8 border border-gray-200 ${!isSectionBComplete ? 'opacity-50 pointer-events-none' : ''}`}>
          <h2 className="text-3xl font-bold text-[#152C47] mb-5 pb-3 border-b-2 border-gray-200 font-['Poppins']">
            About Your Situation
          </h2>
          <p className="text-[#697A90] text-sm mb-6 italic">
            Score each item based on how true it feels in your day-to-day experience, not on how it looks on paper. Think about how your team or organisation behaves on a bad week, not how you'd like it to perform. On this scale: 0 is dismal; 10 is optimal.
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-sm">
            <p className="font-semibold text-gray-800 mb-2">Scoring Guide:</p>
            <div className="grid grid-cols-1 gap-1 text-gray-600">
              <p><strong>0</strong> = We are failing at this. It actively harms progress.</p>
              <p><strong>1–2</strong> = Very weak. This is a critical constraint or blocker.</p>
              <p><strong>3–4</strong> = Weak. We patch it, but it's unreliable.</p>
              <p><strong>5</strong> = Neutral. Works some days, fails others.</p>
              <p><strong>6–7</strong> = Moderate strength. Still fragile under pressure.</p>
              <p><strong>8</strong> = Strong. This supports progress reliably.</p>
              <p><strong>9</strong> = Very strong. Works well even when stressed.</p>
              <p><strong>10</strong> = Exemplary. This is a genuine organisational strength.</p>
            </div>
          </div>

          {/* All 14 Sliders - I'll continue in next message */}
          {/* Slider 1 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">1. Strategic clarity and value case</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score1}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score1}
              onChange={(e) => handleInputChange({ target: { id: 'score1', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              If the business case is woolly, contested or unrealistic, recoverability drops. You can't reliably recover value if no one agrees what the value is. Strong strategic alignment means your efforts compound instead of dispersing.
            </p>
          </div>

          {/* Slider 2 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">2. Portfolio governance and decision rights</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score2}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score2}
              onChange={(e) => handleInputChange({ target: { id: 'score2', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              Even if the case is clear, recoverability suffers when nobody knows who decides what, or when decisions get stuck. Clean governance and decision rights increase the fraction of lost value you can realistically bring back because decisions actually land.
            </p>
          </div>

          {/* Slider 3 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">3. Risk and adaptive planning maturity</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score3}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score3}
              onChange={(e) => handleInputChange({ target: { id: 'score3', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              If risk is treated as a register, not a living practice, then the system under-reacts or over-reacts. Strong risk intelligence means the organisation can pivot instead of freeze or thrash — which increases the proportion of value you can recover mid-flight.
            </p>
          </div>

          {/* Slider 4 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">4. Delivery flow and interdependency management</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score4}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score4}
              onChange={(e) => handleInputChange({ target: { id: 'score4', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              This is where a huge amount of recoverable value hides. Poor interdependency management means initiatives block each other and delay compounding benefits. When you improve flow, the same portfolio yields more, faster — materially lifting the recoverability factor.
            </p>
          </div>

          {/* Slider 5 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">5. Operational performance management and data quality</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score5}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score5}
              onChange={(e) => handleInputChange({ target: { id: 'score5', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              Without trustworthy, timely data, you can't tell whether interventions are working, which slows course corrections and undermines confidence. Strong performance management lets you steer toward recoverable value instead of flying blind.
            </p>
          </div>

          {/* Slider 6 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">6. Change implementation and BA/DM capability</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score6}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score6}
              onChange={(e) => handleInputChange({ target: { id: 'score6', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              If nobody can translate intent into workable change, the organisation keeps reliving the same patterns. Solid BA/DM and change capability dramatically increases how much of the "theoretical" value can be made real within 12–18 months.
            </p>
          </div>

          {/* Slider 7 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">7. Stakeholder network and sponsorship strength</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score7}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score7}
              onChange={(e) => handleInputChange({ target: { id: 'score7', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              Recoverability depends heavily on whether the right people are actively backing the work. If sponsorship is thin or symbolic, change stalls at the point it encounters real trade-offs. Strong stakeholder engagement means less value is lost in the politics.
            </p>
          </div>

          {/* Slider 8 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">8. Leadership and team dynamics</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score8}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score8}
              onChange={(e) => handleInputChange({ target: { id: 'score8', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              If the leadership team is fragmented, anxious or in quiet conflict, a lot of value is lost in avoidance, second-guessing and rework. Healthy dynamics and psychological safety raise your recoverability because tough calls can actually be made and held.
            </p>
          </div>

          {/* Slider 9 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">9. Culture, communication and narrative truth</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score9}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score9}
              onChange={(e) => handleInputChange({ target: { id: 'score9', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              If the official story and lived experience diverge, people quietly opt out. When culture and communication are aligned and honest, more of the "paper" value survives contact with reality. You lose less to cynicism and resistance.
            </p>
          </div>

          {/* Slider 10 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">10. Hidden work and dependence load</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score10}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score10}
              onChange={(e) => handleInputChange({ target: { id: 'score10', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              When a large share of the work is invisible, unrecognised or carried by a few heroic individuals, recoverable value gets trapped. Our diagnostics and method are designed to surface and rebalance this load — which is why, when you address hidden work, the recoverability factor shifts most dramatically.
            </p>
          </div>

          {/* Slider 11 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">11. Organisational complexity and dependency density</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score11}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score11}
              onChange={(e) => handleInputChange({ target: { id: 'score11', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              The more levels, silos, regulatory and cross-cutting programmes, the harder it is to see past noise. Untangled complexity lowers recoverability.
            </p>
          </div>

          {/* Slider 12 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">12. Transformation fatigue and history</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score12}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score12}
              onChange={(e) => handleInputChange({ target: { id: 'score12', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              An organisation on its third "big move" in five years will have lost psychological credit. People discount new promises, which slows recoverability.
            </p>
          </div>

          {/* Slider 13 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">13. Meeting and cadence architecture</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score13}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score13}
              onChange={(e) => handleInputChange({ target: { id: 'score13', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              If the meeting system is chaotic, stale talks every day. Designing fit-for-purpose cadences raises recoverability because the system can self-correct an opportunity more quickly.
            </p>
          </div>

          {/* Slider 14 */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-gray-800">14. Talent availability and critical role stability</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Your score:</span>
                <span className="bg-[#86C2CA] text-[#152C47] px-3 py-1 rounded font-bold min-w-[40px] text-center">
                  {formData.score14}
                </span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.score14}
              onChange={(e) => handleInputChange({ target: { id: 'score14', value: e.target.value } })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#86C2CA]"
              disabled={!isSectionBComplete}
            />
            <p className="text-[#697A90] text-sm mt-2 italic">
              Even with great design, if your critical roles churn or are permanently overloaded, recoverability drops. When key people are stable and supported, more value shows up on schedule.
            </p>
          </div>
        </div>

        {/* Generate Report Button */}
        <div className="text-center">
          <button
            onClick={onGenerateReport}
            disabled={!isFormComplete}
            className="bg-[#86C2CA] hover:bg-[#a0d9e0] text-[#152C47] font-bold py-4 px-8 rounded-lg font-['Poppins'] disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
          >
            Generate Report
          </button>
        </div>
      </div>

            {/* Footer */}
      <div className="max-w-4xl mx-auto px-6 pb-8">
        <div className="border-t border-[#B3C9D3] pt-8 text-center text-sm text-[#697A90]">
          <p>© 2025 Daring Futures Ltd | We surface and resolve hidden work</p>
          <p className="mt-2">
            <a href="https://wiki.daringfutures.com/Working+With+Us/Service+Delivery+Policies/Policy+-+Terms+and+Conditions" className="hover:text-[#86C2CA] transition-colors ml-2">Terms & Conditions</a> |
            <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Privacy+and+Cookies" className="hover:text-[#86C2CA] transition-colors"> Privacy & Cookie Policy</a> |
            <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Licensing+and+Fair+Use" className="hover:text-[#86C2CA] transition-colors ml-2">Fair Use Policy</a> |
            <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Anti-Slavery+Statement" className="hover:text-[#86C2CA] transition-colors ml-2">Anti-Slavery Statement</a>
          </p>
        </div>
      </div>

    </>
  );
}

export default InputForm;